/**
 * 发版后的前端自动更新。
 *
 * 移除 Service Worker 后，页面不再有任何「发现新版本就刷新」的能力：
 * 长期挂着的标签页会一直跑旧代码，发版覆盖部署后旧 chunk 被删除，
 * 触发路由懒加载时会直接抛 ChunkLoadError。这里补上两条：
 *
 * 1. watchChunkLoadError —— 懒加载失败（多半就是发过版了）立即刷新，消除白屏；
 * 2. watchAppVersion     —— 轮询 version.json（构建时由 build-version.js 生成），
 *                           发现版本号变化直接刷新，不做提示。
 *
 * 两者共用 reloadOnce 的两层保护，缺一不可：
 * - 频率下限：同一标签页 60 秒内最多自动刷新一次；
 * - 按目标版本熔断：同一个「目标状态」只自动刷新一次。多节点 version.json 不一致
 *   （灰度发布、CDN 未同步）时，只靠频率下限会让用户按轮询节奏被反复整页刷新、
 *   正在编辑的内容每 5 分钟丢一次；记住已经为哪个版本刷过，就不会再刷第二次。
 *
 * @date 2026-09-01
 * @ai-generated true
 * @ai-tool Claude Code
 */

import { getAppVersionFile } from '@/service/api/resource'
import { logError, logInfo } from './logger'

const RELOAD_AT_KEY = 'appAutoReloadAt'
/* 刷新原因留痕：页面 reload 后控制台会被清空，排查「为什么自己刷新了」时读这个 */
const RELOAD_REASON_KEY = 'appAutoReloadReason'
/* 已经为哪些「目标状态」刷过：chunk 失败记 'chunk'，版本变化记目标版本号。
   同一标签页内不再为同一个 key 重复刷新，这是真正的熔断 */
const RELOADED_KEYS = 'appAutoReloadedKeys'
const MIN_RELOAD_GAP = 60 * 1000
/* 轮询间隔；页面重新可见时也会补查一次，所以不必太密 */
const POLL_INTERVAL = 5 * 60 * 1000
/* 连续失败多少次后放弃轮询：开发环境没有 version.json，靠这个自动收敛 */
const MAX_FAILURES = 3

/* sessionStorage 在隐私模式等场景下可能不可用；读写一律兜底，
   拿不到就退化成「没有历史记录」，宁可多刷一次也不要在这里抛错中断主流程 */
const readReloadedKeys = () => {
  try {
    return String(sessionStorage.getItem(RELOADED_KEYS) || '')
      .split('|')
      .filter(Boolean)
  } catch (error) {
    return []
  }
}

/**
 * 自动刷新页面。
 * @param {string} key    目标状态标识；同一个 key 在本标签页内只刷新一次
 * @param {string} reason 人类可读的刷新原因，写入 sessionStorage 便于事后排查
 */
const reloadOnce = (key, reason) => {
  try {
    // 熔断：这个目标状态已经刷过了，再刷也解决不了问题，只会打断用户
    if (readReloadedKeys().includes(key)) return
    // 频率下限：防止多个触发源在同一瞬间叠加
    const last = Number(sessionStorage.getItem(RELOAD_AT_KEY) || 0)
    if (Date.now() - last < MIN_RELOAD_GAP) return
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()))
    sessionStorage.setItem(RELOAD_REASON_KEY, reason)
    sessionStorage.setItem(RELOADED_KEYS, readReloadedKeys().concat(key).join('|'))
  } catch (error) {
    // sessionStorage 不可用：放弃熔断与防抖，仍然刷新（白屏比多刷一次更糟）
  }
  logInfo('appUpdate', `${reason}，自动刷新页面`)
  window.location.reload()
}

/* ---------------- P0：懒加载失败兜底 ---------------- */

// 各浏览器/构建工具的报错文案不统一，统一按关键词识别
const CHUNK_ERROR_RE =
  /Loading chunk|Loading CSS chunk|ChunkLoadError|dynamically imported module|Importing a module script failed/i

const isChunkLoadError = (error) => {
  if (!error) return false
  const message = typeof error === 'string' ? error : error.message || error.name || ''
  return CHUNK_ERROR_RE.test(String(message))
}

/**
 * 路由懒加载或页面内动态 import 失败时自动刷新一次。
 * @param {import('vue-router').Router} router
 */
export const watchChunkLoadError = (router) => {
  router?.onError?.((error) => {
    if (isChunkLoadError(error)) reloadOnce('chunk', '检测到路由资源加载失败，可能已发布新版本')
  })
  // 页面内部（非路由）的动态 import 失败不会走 router.onError，兜在这里
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event?.reason)) reloadOnce('chunk', '检测到资源加载失败，可能已发布新版本')
  })
}

/* ---------------- P1：版本轮询 ---------------- */

/* 带时间戳并声明 no-cache（见 getAppVersionFile）：CDN 与浏览器都不能给这个请求返回缓存，
   否则轮询永远看不到新版本；非 2xx 时 axios 直接 reject */
const fetchVersion = async () => {
  const data = await getAppVersionFile()
  if (!data?.version) throw new Error('version.json 缺少 version 字段')
  return String(data.version)
}

/**
 * 轮询构建版本号，发现变化直接刷新页面（不提示）。
 * 首次取到的值作为基线，所以本次访问加载的就是当前线上版本。
 */
export const watchAppVersion = () => {
  let baseline = null
  let failures = 0
  let timer = null
  /* 停止标记：仅清 interval 是不够的，visibilitychange 仍会调用 check()，
     熔断后每次切回前台都会再打一次请求、再记一条错误日志 */
  let stopped = false

  const check = async () => {
    if (stopped) return
    // 页面不可见时不发请求，切回来时由 visibilitychange 立即补查
    if (document.hidden) return
    try {
      const version = await fetchVersion()
      failures = 0
      if (baseline === null) {
        baseline = version
        return
      }
      if (version !== baseline) {
        reloadOnce(`version:${version}`, `检测到新版本（${baseline} → ${version}）`)
      }
    } catch (error) {
      failures += 1
      // 开发环境没有 version.json，连续失败即停，不再制造无用请求
      if (failures >= MAX_FAILURES) {
        stop()
        logError('appUpdate', error)
      }
    }
  }

  const onVisibilityChange = () => {
    if (!document.hidden) check()
  }

  // 停止后必须把监听器一并摘掉，否则它会一直持有 check 与本闭包
  function stop() {
    stopped = true
    if (timer) clearInterval(timer)
    timer = null
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }

  check()
  timer = setInterval(check, POLL_INTERVAL)
  document.addEventListener('visibilitychange', onVisibilityChange)

  // 交回停止入口，便于调用方（或测试）主动收敛
  return stop
}
